import { formatInTimeZone } from "date-fns-tz";

interface TimeResponse {
  success: boolean;
  timestamp: string;
  unix: number;
}

class UniversalTimeService {
  private static instance: UniversalTimeService;
  private timeOffset: number = 0;
  private lastSync: number = 0;
  private readonly SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private readonly API_BASE =
    import.meta.env.VITE_API_URL || "https://flowcus-api-prod.prizio.workers.dev";

  private constructor() {}

  static getInstance(): UniversalTimeService {
    if (!UniversalTimeService.instance) {
      UniversalTimeService.instance = new UniversalTimeService();
    }
    return UniversalTimeService.instance;
  }

  private async syncWithServer(): Promise<void> {
    try {
      const clientTimeBefore = Date.now();
      const response = await fetch(`${this.API_BASE}/api/v1/time`);
      const clientTimeAfter = Date.now();

      if (!response.ok) {
        throw new Error(`Failed to fetch server time: ${response.status}`);
      }

      const data: TimeResponse = await response.json();

      if (!data.success) {
        throw new Error("Server time fetch unsuccessful");
      }

      // Account for network latency by using the average of before/after times
      const networkLatency = (clientTimeAfter - clientTimeBefore) / 2;
      const adjustedClientTime = clientTimeBefore + networkLatency;

      // Calculate offset: server time - adjusted client time
      this.timeOffset = data.unix - adjustedClientTime;
      this.lastSync = Date.now();

      console.log(`Time sync complete. Offset: ${this.timeOffset}ms, Latency: ${networkLatency}ms`);
    } catch (error) {
      console.warn("Failed to sync with server time, using client time:", error);
      // Reset offset to 0 if sync fails
      this.timeOffset = 0;
      this.lastSync = Date.now();
    }
  }

  private shouldSync(): boolean {
    return Date.now() - this.lastSync > this.SYNC_INTERVAL;
  }

  /**
   * Get the current accurate timestamp as ISO string
   */
  async getCurrentTimestamp(): Promise<string> {
    if (this.shouldSync()) {
      await this.syncWithServer();
    }

    const accurateTime = Date.now() + this.timeOffset;
    return new Date(accurateTime).toISOString();
  }

  /**
   * Get the current accurate timestamp as Unix milliseconds
   */
  async getCurrentUnix(): Promise<number> {
    if (this.shouldSync()) {
      await this.syncWithServer();
    }

    return Date.now() + this.timeOffset;
  }

  /**
   * Initialize the time service (call once at app startup)
   */
  async initialize(): Promise<void> {
    await this.syncWithServer();
  }

  /**
   * Get time offset for debugging
   */
  getTimeOffset(): number {
    return this.timeOffset;
  }

  /**
   * Force a sync with the server
   */
  async forceSync(): Promise<void> {
    await this.syncWithServer();
  }

  /**
   * Get current time in user's timezone
   */
  async getCurrentTimeInTimezone(timezone: string): Promise<{
    utc: string;
    local: string;
    timezone: string;
    formatted: string;
  }> {
    const utcTime = await this.getCurrentTimestamp();
    const localTime = formatInTimeZone(utcTime, timezone, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
    const formatted = formatInTimeZone(utcTime, timezone, "EEEE, MMMM do, yyyy 'at' h:mm a zzz");

    return {
      utc: utcTime,
      local: localTime,
      timezone,
      formatted,
    };
  }

  /**
   * Parse natural language date in user's timezone
   */
  parseUserDate(dateString: string, userTimezone: string): string {
    // Simple parsing - in a real implementation, you might use a library like chrono-node
    const now = new Date();
    let targetDate: Date;

    const lowerDateString = dateString.toLowerCase().trim();

    if (lowerDateString === "today") {
      // Get today in user's timezone
      const todayInUserTz = formatInTimeZone(now, userTimezone, "yyyy-MM-dd");
      targetDate = new Date(todayInUserTz + "T09:00:00"); // Default to 9 AM
    } else if (lowerDateString === "tomorrow") {
      // Get tomorrow in user's timezone
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const tomorrowInUserTz = formatInTimeZone(tomorrow, userTimezone, "yyyy-MM-dd");
      targetDate = new Date(tomorrowInUserTz + "T09:00:00"); // Default to 9 AM
    } else if (lowerDateString.includes("next week")) {
      const nextWeek = new Date(now);
      nextWeek.setDate(now.getDate() + 7);
      const nextWeekInUserTz = formatInTimeZone(nextWeek, userTimezone, "yyyy-MM-dd");
      targetDate = new Date(nextWeekInUserTz + "T09:00:00"); // Default to 9 AM
    } else if (lowerDateString.includes("this evening") || lowerDateString.includes("tonight")) {
      const todayInUserTz = formatInTimeZone(now, userTimezone, "yyyy-MM-dd");
      targetDate = new Date(todayInUserTz + "T18:00:00"); // Default to 6 PM
    } else if (lowerDateString.includes("this afternoon")) {
      const todayInUserTz = formatInTimeZone(now, userTimezone, "yyyy-MM-dd");
      targetDate = new Date(todayInUserTz + "T14:00:00"); // Default to 2 PM
    } else {
      // Try to parse as regular date
      targetDate = new Date(dateString);
      if (isNaN(targetDate.getTime())) {
        // Fallback to today in user's timezone
        const todayInUserTz = formatInTimeZone(now, userTimezone, "yyyy-MM-dd");
        targetDate = new Date(todayInUserTz + "T09:00:00");
      }
    }

    // Convert the target date to UTC for storage
    return targetDate.toISOString();
  }
}

// Export singleton instance
export const timeService = UniversalTimeService.getInstance();
